<layout-page-container>
  <layout-header-container title="Events"/>

  <layout-form-container (clear)="clear()"
                         (load)="loadCreateData()" (searchValueChange)="filter($event)"
                         (submit)="create()" [(searchValue)]="search" [element]="name"
                         [submitDisabled]="creationBlocked"
                         [isCollapsed]="true" [permission]="PermissionService.Inventory_Event_AddMany()"
                         clearText="Felder zurücksetzen"
                         submitText="Event anlegen">
    <div class="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
      <!-- #664 — the first row shares six columns between Name, the sometimes-present kind picker, Start and
           Ende, and the hard rule is that **Start/Ende never change row because the picker appeared or
           vanished**. Only the NAME field flexes: it absorbs exactly the picker's columns when the picker is
           gone, so every span after it lands identically either way.

             md/lg  with picker: 3 + 3 | 3 + 3   — dates on row 2
             md/lg  without:     6     | 3 + 3   — dates on row 2 (Name took the picker's 3)
             xl     with picker: 2 + 2 + 1 + 1   — dates on row 1
             xl     without:     4     + 1 + 1   — dates on row 1 (Name took the picker's 2)

           Before this, Name was fixed at md:2 and the dates at md:2, so losing the picker left 2+2+2 = one row
           and the date fields jumped up beside the name — reported at ~1245 px, which is the `lg` range and
           therefore inherits the `md` spans. -->
      <div class="sm:col-span-6"
           [class.md:col-span-3]="kindSelectorVisible" [class.xl:col-span-2]="kindSelectorVisible"
           [class.md:col-span-6]="!kindSelectorVisible" [class.xl:col-span-4]="!kindSelectorVisible">
        <label class="block text-sm font-medium leading-6 text-gray-900" for="event-name">Name</label>
        <input #name [(ngModel)]="newEvent.name"
               autocomplete="item-name"
               class="mt-2 block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
               id="event-name" name="event-name"
               type="text">
      </div>
      <!-- #664 — "Art des Events", a segmented tab-picker (`cqme-segmented-choice`), sitting between the name
           and the window it applies to: the kind is a property of the event being described, so it belongs
           with the other things being described, not after them.

           A segmented RADIO group, never `app-tab-selection`: a tablist is for peer views of a page and puts
           the choice in the URL, while this choice belongs to the form's state and changes which fields the
           form shows (see the style guide, "Segmented choice inside a form"). It is also never a native
           <select>.

           The picker disappears — rather than disabling — when the contract leaves one choice: a two-option
           control whose second option the server silently overrides is worse than none. The notice block at
           the foot of the form then says why. -->
      @if (kindSelectorVisible) {
        <div class="sm:col-span-6 md:col-span-3 xl:col-span-2">
          <cqme-segmented-choice id="event-kind" legend="Art des Events" name="event-kind"
                                 [options]="kindOptions" [value]="kind"
                                 (valueChange)="onKindChanged($event)"/>
        </div>
      }

      <!-- Fixed spans, deliberately: see the Name field above. A date field whose width depends on the
           picker is a date field that can change row when the picker does. -->
      <div class="sm:col-span-3 md:col-span-3 xl:col-span-1">
        <label class="block text-sm font-medium leading-6 text-gray-900" for="event-start">Start</label>
        <!-- The COMPANY wall clock, not the browser's: 20:00 means 20:00 at the company, whoever types it. -->
        <input [ngModel]="wallClockValue(newEvent.start)" (ngModelChange)="setWallClockValue(newEvent.start, $event); onWindowChanged()"
               data-testid="event-start"
               class="mt-2 block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
               id="event-start" name="event-start"
               type="datetime-local">
      </div>
      <div class="sm:col-span-3 md:col-span-3 xl:col-span-1">
        <label class="block text-sm font-medium leading-6 text-gray-900" for="event-end">Ende</label>
        <input [ngModel]="wallClockValue(newEvent.end)" (ngModelChange)="setWallClockValue(newEvent.end, $event); onWindowChanged()"
               data-testid="event-end"
               class="mt-2 block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
               id="event-end" name="event-end"
               type="datetime-local">
      </div>

      <div class="self-start gap-y-4 sm:col-span-6 md:col-span-3 xl:col-span-2 grid grid-cols-subgrid">
        <!-- #664: a virtual event has no registers, so it has no location configuration either — the block is
             removed rather than disabled. What was entered stays in the model (picking "Virtuelles Event" must
             not destroy work) and comes back when the kind goes back to Kassen-Event; `createIn()` strips it
             from the outgoing batch while the kind is Virtual. -->
        @if (allowedConfigureLocations && !isVirtual) {
          <layout-form-expandable class="sm:col-span-6 md:col-span-3 xl:col-span-2 self-start"
                                  expandText="Preisprofile festlegen"
                                  collapseText="Preisprofile zurücksetzen"
                                  [expanded]="newEvent.configurations != undefined"
                                  (onExpand)="newEvent.configurationsAdd()"
                                  (onCollapse)="newEvent.configurationsRemove()">
            @if (newEvent.configurations) {
              <label class="block text-sm font-medium leading-6 text-gray-900">Alle Verkaufspunkte</label>
              <ng-select [(ngModel)]="newEvent.allConfigurations"
                         (ngModelChange)="newEvent.setConfigurations(locations, $event)" class="ng-select cheqme-style">
                @for (configuration of configurations; track configuration.id) {
                  <ng-option [value]="configuration.id">{{ configuration.name }}</ng-option>
                }
              </ng-select>

              <div class="border-b border-gray-300 pt-2"></div>

              <table class="border-separate border-spacing-x-3 border-spacing-y-1 -mx-3">
                <thead class="sr-only">
                <tr>
                  <th>Standort</th>
                  <th>Preisprofil</th>
                </tr>
                </thead>
                <tbody>
                  @for (location of locations; track location.id) {
                    <tr>
                      <td>{{ location.name }}</td>
                      <td class="w-full">
                        <ng-select [(ngModel)]="newEvent.configurations[location.id]"
                                   (ngModelChange)="newEvent.setConfiguration(location.id, $event)"
                                   class="ng-select cheqme-style">
                          @for (configuration of configurations; track configuration.id) {
                            <ng-option [value]="configuration.id">{{ configuration.name }}
                            </ng-option>
                          }
                        </ng-select>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            }
            <svg icon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
              <path
                d="M128 88a40 40 0 1 0 40 40a40 40 0 0 0-40-40m0 64a24 24 0 1 1 24-24a24 24 0 0 1-24 24m112-96H16a8 8 0 0 0-8 8v128a8 8 0 0 0 8 8h224a8 8 0 0 0 8-8V64a8 8 0 0 0-8-8m-46.35 128H62.35A56.78 56.78 0 0 0 24 145.65v-35.3A56.78 56.78 0 0 0 62.35 72h131.3A56.78 56.78 0 0 0 232 110.35v35.3A56.78 56.78 0 0 0 193.65 184M232 93.37A40.8 40.8 0 0 1 210.63 72H232ZM45.37 72A40.8 40.8 0 0 1 24 93.37V72ZM24 162.63A40.8 40.8 0 0 1 45.37 184H24ZM210.63 184A40.8 40.8 0 0 1 232 162.63V184Z"/>
            </svg>

            @if (!canConfigureLocations) {
              <div overlay class="absolute inset-0">
                <div class="bg-gray-200 absolute inset-0"></div>
                <div class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                  <div class="h-8 w-8 animate-spin text-theme-primary-700">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
                      <path
                        d="M140 32v32a12 12 0 0 1-24 0V32a12 12 0 0 1 24 0m84 84h-32a12 12 0 0 0 0 24h32a12 12 0 0 0 0-24m-42.26 48.77a12 12 0 1 0-17 17l22.63 22.63a12 12 0 0 0 17-17ZM128 180a12 12 0 0 0-12 12v32a12 12 0 0 0 24 0v-32a12 12 0 0 0-12-12m-53.74-15.23L51.63 187.4a12 12 0 0 0 17 17l22.63-22.63a12 12 0 1 0-17-17M76 128a12 12 0 0 0-12-12H32a12 12 0 0 0 0 24h32a12 12 0 0 0 12-12m-7.4-76.37a12 12 0 1 0-17 17l22.66 22.6a12 12 0 0 0 17-17Z"/>
                    </svg>
                  </div>
                </div>
              </div>
            }
          </layout-form-expandable>
        }
      </div>

      <div class="self-start gap-y-4 sm:col-span-6 md:col-span-3 xl:col-span-4 grid grid-cols-subgrid">
        <layout-form-expandable (onCollapse)="newEvent.optionsRemove()"
                                (onExpand)="newEvent.optionsAdd()" [expanded]="newEvent.options != undefined"
                                class="sm:col-span-6 md:col-span-3 xl:col-span-2"
                                collapseText="Optionen zurücksetzen"
                                expandText="Weitere Optionen">
          @if (newEvent.options) {
            <div class="inline-flex gap-x-2">
              <button id="event-public" name="event-public"
                      (click)="newEvent.options.isPublic = !newEvent.options.isPublic"
                      [class.!bg-theme-primary-700]="newEvent.options.isPublic" type="button"
                      class="disabled:opacity-25 bg-gray-200 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-theme-primary-700 focus:ring-offset-2"
                      role="switch" aria-checked="true">
                <!-- Enabled: "translate-x-5", Not Enabled: "translate-x-0" -->
                <span aria-hidden="true" [class.!translate-x-5]="newEvent.options.isPublic"
                      class="translate-x-0 pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"></span>
              </button>
              <label for="event-public"
                     class="block text-sm font-medium leading-6 text-gray-900 select-none">Freigegeben für
                Mitarbeiter</label>
            </div>
            <!-- „Im Dienstplan ausblenden" — the same switch pattern as „Freigegeben für Mitarbeiter" above,
                 so the two options in this block read as one pair rather than as two controls that happen to
                 sit together. It is about the Dienstplan ONLY: the event stays in the events list, the reports
                 and (if public) the employee calendar. -->
            <div class="inline-flex gap-x-2 pt-3">
              <button id="event-hide-in-shift-maker" name="event-hide-in-shift-maker"
                      (click)="newEvent.options.hideInShiftMaker = !newEvent.options.hideInShiftMaker"
                      [class.!bg-theme-primary-700]="newEvent.options.hideInShiftMaker" type="button"
                      class="disabled:opacity-25 bg-gray-200 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-theme-primary-700 focus:ring-offset-2"
                      role="switch" [attr.aria-checked]="newEvent.options.hideInShiftMaker">
                <span aria-hidden="true" [class.!translate-x-5]="newEvent.options.hideInShiftMaker"
                      class="translate-x-0 pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"></span>
              </button>
              <label for="event-hide-in-shift-maker"
                     class="block text-sm font-medium leading-6 text-gray-900 select-none">Im Dienstplan
                ausblenden</label>
            </div>

            <span class="pt-3 block text-sm font-medium leading-6 text-gray-900">Anmeldeschluss Mitarbeiter vor Event (in Tagen)</span>
            <input [(ngModel)]="newEvent.options.eventResponseDays"
                   type="number" step="1" min="0" name="event-repetition-end" id="event-response-end"
                   class="text-center mt-2 block rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6">

            <span class="mt-3 block text-sm font-medium leading-6 text-gray-900"></span>
            <div class="inline-flex gap-x-2 mt-2">
            </div>
          }
        </layout-form-expandable>
        <layout-form-expandable (onCollapse)="newEvent.repetitionRemove()"
                                (onExpand)="newEvent.repetitionAdd()"
                                [expanded]="newEvent.repetition != undefined"
                                class="sm:col-span-6 md:col-span-3 xl:col-span-2"
                                collapseText="Wiederholung entfernen" expandText="Wiederholung hinzufügen">
          <svg class="h-4 w-4" icon viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M24 80a8 8 0 0 1 4-6.91l96-56a8 8 0 0 1 8.06 0l96 56a8 8 0 0 1 0 13.82l-96 56a8 8 0 0 1-8.06 0l-96-56A8 8 0 0 1 24 80m196 41.09l-92 53.65l-92-53.65a8 8 0 0 0-8 13.82l96 56a8 8 0 0 0 8.06 0l96-56a8 8 0 1 0-8.06-13.82M232 192h-16v-16a8 8 0 0 0-16 0v16h-16a8 8 0 0 0 0 16h16v16a8 8 0 0 0 16 0v-16h16a8 8 0 0 0 0-16m-92 23.76l-12 7l-92-53.67a8 8 0 0 0-8 13.82l96 56a8 8 0 0 0 8.06 0l16-9.33a8 8 0 1 0-8.06-13.82"
            />
          </svg>

          @if (newEvent.repetition) {
            <span class="block text-sm font-medium leading-6 text-gray-900">Wochentage</span>
            <div class="inline-flex gap-x-2 mt-2">
              <button *ngFor="let weekday of weekdays" type="button"
                      class="h-8 w-8 rounded-full bg-white border-theme-primary-400 text-theme-primary-700"
                      [ngClass]="{'!bg-theme-primary-700 !text-white': newEvent.repetition.contains(parseInt(weekday))}"
                      (click)="newEvent.repetition.set(parseInt(weekday))">
                <span>{{ WeekdayLabels[parseInt(weekday)][0] }}</span>
              </button>
            </div>

            <span class="pt-3 block text-sm font-medium leading-6 text-gray-900">Intervall</span>
            <div class="flex flex-wrap gap-2 items-center mt-2">
              <span>Alle</span>
              <input type="number" step="1" min="1" [(ngModel)]="newEvent.repetition.intervalCount"
                     class="text-center block rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6">
              <ng-select [(ngModel)]="newEvent.repetition.interval" [clearable]="false"
                         class="ng-select cheqme-style text-center block rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6">
                @for (interval of intervals; track parseInt(interval)) {
                  <ng-option [value]="parseInt(interval)">{{ IntervalLabels[parseInt(interval)] }}
                  </ng-option>
                }
              </ng-select>
            </div>

            <span class="pt-3 block text-sm font-medium leading-6 text-gray-900">Ende (inkludiert)</span>
            <input [(ngModel)]="newEvent.repetition.end.input_date"
                   type="date" name="event-repetition-end" id="event-repetition-end"
                   class="mt-2 block rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6">

            <span class="pt-3 block text-sm font-medium leading-6 text-gray-900">Format Datums-Präfix</span>
            <ng-select [(ngModel)]="newEvent.repetition.dateFormat" [clearable]="false"
                       type="date" name="event-repetition-end" id="event-repetition-format"
                       class="ng-select cheqme-style mt-2 block rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6">
              @for (format of formats; track format) {
                <ng-option [value]="parseInt(format)">{{ DateFormatLabels[parseInt(format)] }}
                </ng-option>
              }
            </ng-select>
          }
        </layout-form-expandable>
      </div>
    </div>

    <!-- #664 — one hint slot, four mutually exclusive states: refused, forced-virtual, real-blocked by an
         overlap, or how much of the virtual allowance is left.

         It sits BELOW the three option expandables and OUTSIDE the field grid, spanning the full width, right
         above the footer row. Two reasons, and neither is cosmetic:

         * A message parked beside a field reads as that field's error, and only one of these four is about
           the kind picker at all. Under everything it reads as what it is — a statement about the form as a
           whole, immediately above the button it governs.
         * Outside the grid it costs nothing when there is no hint. As a grid item it would still occupy a row,
           so the form's `gap-y-4` would leave 16 px of blank above the footer on every tenant that never
           licensed virtual events — which is the common case, and also the state the form is in for the first
           300 ms of every debounce while the allowance is still in flight.

         The wrapper therefore carries no spacing of its own (an empty div with margins is the same gap by
         another name); each notice brings its own `mt-4 mb-1`. That is 16 px above — the grid's own row gap —
         and 4 px + the form container's `pb-3` = 16 px below, so the block sits with equal room on both sides.

         `cqme-inline-notice` owns the box (geometry, palette, glyph, ARIA role: `alert` for the refusal,
         `status` for the rest). The ids stay HERE, because the kind spec, the stories and the docs screenshot
         harness all address these four states by id. -->
    <div id="event-kind-hint">
      @if (creationBlocked) {
        <cqme-inline-notice class="mt-4 mb-1" id="event-kind-refusal" tone="error">{{ refusalReason }}
        </cqme-inline-notice>
      } @else if (virtualForced) {
        <cqme-inline-notice class="mt-4 mb-1" id="event-kind-forced" tone="warning">
          Ihr Vertrag enthält in diesem Zeitraum keine Geräte. Das Event wird als virtuelles Event angelegt.
        </cqme-inline-notice>
      } @else if (realBlockedByOverlap) {
        <cqme-inline-notice class="mt-4 mb-1" id="event-kind-real-blocked" tone="warning">
          In diesem Zeitraum läuft bereits ein Kassen-Event. Es kann nur ein virtuelles Event angelegt werden.
        </cqme-inline-notice>
      } @else if (virtualExhaustedText) {
        <!-- The mirror of the state above: the contract licenses virtual events and every slot for this window
             is taken, so the picker vanished and only a Kassen-Event is left. Without this the form silently
             created a Kassen-Event for somebody who had asked for a virtual one. -->
        <cqme-inline-notice class="mt-4 mb-1" id="event-kind-virtual-exhausted" tone="warning">
          {{ virtualExhaustedText }}
        </cqme-inline-notice>
      } @else if (virtualAvailabilityText) {
        <cqme-inline-notice class="mt-4 mb-1" id="event-kind-remaining" tone="info">
          {{ virtualAvailabilityText }}
        </cqme-inline-notice>
      }
    </div>
  </layout-form-container>

  <div *ngIf="hasDownloadPermission" class="flex w-full mb-3">
    <div class="flex divide-x divide-gray-300 place-self-center">
      @if (selection.selectionMode == SelectionMode.None) {
        <a class="text-theme-primary-700 hover:text-theme-primary-900" (click)="selection.toggleMode()">
          Mehrere auswählen
        </a>
      } @else {
        <a class="text-theme-primary-700 hover:text-theme-primary-900 pr-2"
           (click)="selection.toggleMode()">
          Auswahl beenden
        </a>
        <div class="flex place-self-center px-2 gap-x-2">
          <div class="place-self-center">
            <input type="checkbox" name="combined-file" id="combined-file" [(ngModel)]="download_combined">
          </div>
          <label for="combined-file">Kombinierte Datei</label>
        </div>
        <div class="flex place-self-center px-2">
          <a class="text-theme-primary-700 hover:text-theme-primary-900"
             [ngClass]="{'!text-gray-700 pointer-events-none': selection.items.length == 0}"
             (click)="selectSupplier()">
            Herunterladen
          </a>
        </div>
      }
    </div>
    <div class="ml-auto place-self-center">
      <span class="block text-sm font-medium leading-6 text-gray-900">Anzahl Einträge</span>
      <ng-select [(ngModel)]="last" (ngModelChange)="onPageSizeChange()" [clearable]="false"
                 class="ng-select cheqme-style text-center block rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6">
        @for (count of counts; track count) {
          <ng-option [value]="count">{{ count < 2000000000 ? count : "Alle" }}</ng-option>
        }
      </ng-select>
    </div>
  </div>

  <layout-page-scrollable>
    <div class="">
      <div class="mt-2 flow-root">
        <div class="">
          <div class="inline-block min-w-full py-2 align-middle">
            <table class="min-w-full border-separate border-spacing-0">
              <thead>
              <tr>
                <th *ngIf="selection.selectionMode == SelectionMode.Multiple"
                    class="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter sm:pl-6 lg:pl-8"
                    scope="col">
                  <span class="sr-only">Auswahl</span>
                </th>
                <th
                  class="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter sm:pl-6 lg:pl-8"
                  scope="col">
                  Name
                </th>
                <th
                  class="hidden sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                  scope="col">
                  ID
                </th>
                <th
                  class="sticky top-0 z-10 hidden border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter lg:table-cell"
                  scope="col">
                  Freigabe
                </th>
                <th
                  class="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                  scope="col">
                </th>
                <th *ngIf="selection.selectionMode == SelectionMode.None && hasExtendedPermission"
                    class="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 py-3.5 pl-3 pr-4 backdrop-blur backdrop-filter sm:pr-6 lg:pr-8"
                    scope="col">
                  <span class="sr-only">View</span>
                </th>
              </tr>
              </thead>
              <tbody>
                @if (events === undefined) {
                  <app-loading></app-loading>
                } @else if (events === null) {
                  Laden fehlgeschlagen
                } @else {
                  @for (event of events; track event.id) {
                    <tr [id]="event.id"
                        class="hover:bg-gray-100 cursor-pointer relative tr-safari-relative"
                        (click)="show(event)">
                      <td *ngIf="selection.selectionMode == SelectionMode.Multiple"
                          class="whitespace-nowrap border-b border-gray-200 py-2 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 lg:pl-8">
                        <input type="checkbox"
                               class="h-4 w-4 text-theme-primary-600 focus:ring-theme-primary-500"
                               [ngModel]="selection.isSelected(event)">
                        <div class="absolute inset-0 z-20"
                             (click)="selection.toggle(event)"></div>
                      </td>
                      <td
                        class="whitespace-nowrap border-b border-gray-200 py-2 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 lg:pl-8">
                        <div class="flex flex-col">
                          <span class="inline-flex flex-wrap items-center gap-x-2">
                            {{ event.name }}
                            <!-- #664: only the virtual kind is chipped — a Kassen-Event is the norm and a
                                 badge on every row would say nothing. `align-top` because an inline-flex chip
                                 next to larger text otherwise hangs ~1.5px low (style guide, "Status badge"). -->
                            @if (event.kind === EventKind.Virtual) {
                              <span class="inline-flex flex-none items-center rounded-full bg-indigo-50 px-2 py-0.5 align-top text-xs font-semibold text-indigo-700">
                                Virtuell
                              </span>
                            }
                          </span>
                          <span *ngIf="!event.publicTitle.isNullOrWhitespace()"
                                class="text-gray-500">
                          {{ event.publicTitle }}
                        </span>
                        </div>
                      </td>
                      <td
                        class="hidden whitespace-nowrap border-b border-gray-200 px-3 py-2 text-sm text-gray-500 truncate text-ellipsis">
                        {{ event.id }}
                      </td>
                      <td
                        class="hidden whitespace-nowrap border-b border-gray-200 px-3 py-2 text-sm text-gray-500 lg:table-cell">
                        @if (event.public) {
                          <span
                            class="">Anmeldung bis {{ event.publicResponseEnd | date: "dd.MM.yyyy HH:mm" }}
                            Uhr</span>
                        } @else {
                          <span class="text-red-700">nicht freigegeben</span>
                        }
                      </td>
                      <td
                        class="whitespace-nowrap border-b border-gray-200 px-3 py-2 text-sm text-gray-500">
                        <!--<div class="grid grid-cols-2 gap-1">
                          <span class="text-right">Beginn:</span>
                          <span class="font-light">{{ event.start | date: "dd.MM.yyyy HH:mm" }}
                            Uhr</span>
                          <span class="text-right">Ende:</span>
                          <span class="font-light">{{ event.end | date: "dd.MM.yyyy HH:mm" }}
                            Uhr</span>
                        </div>-->
                        <div class="inline-flex gap-x-1">
                          <div class="flex flex-col">
                            <span class="text-right">Start:</span>
                            <span class="text-right">Ende:</span>
                          </div>
                          <div class="flex flex-col">
                            <span class="font-light">{{ event.start | date: "dd.MM.yyyy HH:mm" }}
                              Uhr</span>
                            <span class="font-light">{{ event.end | date: "dd.MM.yyyy HH:mm" }}
                              Uhr</span>
                          </div>
                        </div>
                      </td>
                      <td *ngIf="selection.selectionMode == SelectionMode.None && hasExtendedPermission"
                          class="whitespace-nowrap border-b border-gray-200 py-2 pl-3 text-right text-sm font-medium pr-3">
                        <div class="flex">
                          <a href="#" class="text-right ml-auto">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256"
                                 class="h-6 w-6">
                              <path
                                d="m184.49 136.49l-80 80a12 12 0 0 1-17-17L159 128L87.51 56.49a12 12 0 1 1 17-17l80 80a12 12 0 0 1-.02 17"/>
                            </svg>
                            <span class="sr-only">, {{ event.name }}</span>
                          </a>
                        </div>
                      </td>
                    </tr>
                  }
                }
              <!-- More people... -->
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </layout-page-scrollable>
</layout-page-container>

<app-supplier-popup (selectedChange)="download($event)" *ngIf="hasDownloadPermission"
                    [(visible)]="supplierPopup_isVisible"></app-supplier-popup>
@if (new_events_grouped) {
  <layout-confirm-popup confirmText="Erstellen" cancelText="Abbrechen" title="Events überprüfen"
                        (onSubmit)="create_send(new_events_grouped)"
                        (onCancel)="new_events_grouped = undefined; dryRunVerdicts = undefined">
    <div class="flex flex-col">
      <!-- #664: the verdicts come from `AddMany?dryRun=true`, so this preview shows what the server WOULD
           create — including occurrences a contract boundary turns virtual. Kind is immutable afterwards. -->
      <location-weekday-view [events_grouped]="new_events_grouped"
                             [verdicts]="dryRunVerdicts"></location-weekday-view>
    </div>
  </layout-confirm-popup>
}

